import { useRef } from 'react';

import produce from 'immer';
import create from 'zustand';
import { persist } from 'zustand/middleware';

type Id = string | number;
type IdBooleanMap = {
  [id in Id]: boolean;
};

const hasPersistedState = (persistKey: string): boolean => {
  try {
    return Boolean(localStorage.getItem(persistKey));
  } catch {
    return false;
  }
};

const assertArray = (input: Id | Id[]) => (Array.isArray(input) ? input : [input]);

const createPersistStore = (persistKey: string, defaultState: IdBooleanMap = {}) =>
  create(persist<IdBooleanMap>(() => defaultState, { name: persistKey }));

const createSimpleStore = (defaultState: IdBooleanMap = {}) =>
  create<IdBooleanMap>(() => defaultState);

export const createSelectionStore = ({
  persistKey,
  defaultState,
}: {
  persistKey?: string;
  defaultState?: IdBooleanMap;
} = {}) => {
  const useStore = persistKey
    ? createPersistStore(persistKey, defaultState)
    : createSimpleStore(defaultState);
  const toggle = (id: Id) => useStore.setState((state) => ({ [id]: !state[id] }));
  const add = (ids: Id | Id[]) =>
    useStore.setState(produce((state) => assertArray(ids).forEach((id) => (state[id] = true))));
  const clear = () => useStore.setState({}, true);
  const isSelected = (state: IdBooleanMap, ids: Id | Id[]) =>
    assertArray(ids).findIndex((id) => !state[id]) === -1;
  return { useStore, toggle, add, clear, isSelected };
};
export type SelectionStore = ReturnType<typeof createSelectionStore>;

export const useSelectionStore = (persistKey?: string, defaultState?: IdBooleanMap) => {
  const selectionStore = useRef(
    createSelectionStore({
      persistKey,
      defaultState: persistKey
        ? !hasPersistedState(persistKey)
          ? defaultState
          : undefined
        : defaultState,
    })
  ).current;
  const store = selectionStore.useStore();
  return { ...selectionStore, isSelected: (id: Id) => Boolean(store[id]) };
};
